// src/pages/TextTools/QuizGenerator.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generateQuiz, checkQuizStatus, downloadQuiz } from '../../services/chatApi';
import { Download, FileText, Loader2, Upload } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};


const QuizGenerator = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const [inputMethod, setInputMethod] = useState('none');
    const [subject, setSubject] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [numQuizzes, setNumQuizzes] = useState(1);
    const [questionsPerQuiz, setQuestionsPerQuiz] = useState(5);
    const [allQuizzes, setAllQuizzes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [error, setError] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });

    // Effect to poll task status when taskId is set
    useEffect(() => {
        let interval = null;
        if (taskId && taskStatus === 'processing') {
            interval = setInterval(async () => {
                try {
                    const statusResponse = await checkQuizStatus(taskId);
                    setTaskStatus(statusResponse.status);

                    if (statusResponse.status === 'completed') {
                        setAllQuizzes(statusResponse.quizzes);
                        clearInterval(interval);
                        
                        // Update credits if returned
                        if (statusResponse.new_credits !== undefined) {
                            console.log(`Credits updated. New balance: ${statusResponse.new_credits}`);
                            setCredits(statusResponse.new_credits);
                        }
                        
                        setTaskId(null);
                    } else if (statusResponse.status === 'failed') {
                        setError(statusResponse.error || t('quizGenerator.errorGeneric'));
                        clearInterval(interval);
                        setTaskId(null);
                    } else if (statusResponse.status === 'processing') {
                        setProgress({
                            current: statusResponse.current || 0,
                            total: statusResponse.total || 0,
                            message: statusResponse.progress_message || t('quizGenerator.processing')
                        });
                    }
                } catch (err) {
                    console.error('Error checking task status:', err);
                    clearInterval(interval);
                    setTaskId(null);
                    setError(t('quizGenerator.statusCheckError'));
                }
            }, 2000); // Check every 2 seconds
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [taskId, taskStatus, t, setCredits]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setError(t('quizGenerator.fileTooLarge'));
                return;
            }
            setUploadedFile(file);
            setError(null);
        }
    };

    const handleGenerate = async () => {
        setError(null);
        setAllQuizzes([]);
        setTaskId(null);
        setTaskStatus('processing');
        setProgress({ current: 0, total: 0, message: t('quizGenerator.startingGeneration') });

        if (inputMethod === 'file' && !uploadedFile) {
            setError(t('quizGenerator.uploadFileFirst'));
            return;
        }

        if (inputMethod === 'text' && !subject.trim()) {
            setError(t('quizGenerator.enterSubjectFirst'));
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('num_quizzes', numQuizzes);
            formData.append('questions_per_quiz', questionsPerQuiz);
            formData.append('input_method', inputMethod);

            if (inputMethod === 'file') {
                formData.append('file', uploadedFile);
            } else {
                formData.append('subject', subject);
            }

            const result = await generateQuiz(formData);
            
            if (result.task_id) {
                setTaskId(result.task_id);
            }
        } catch (err) {
            setError(err.message || t('quizGenerator.errorGeneric'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (format) => {
        try {
            const blob = await downloadQuiz(allQuizzes, format);
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            const extensions = {
                'json': 'json',
                'docx': 'docx',
                'pdf': 'pdf',
                'pptx': 'pptx'
            };
            
            a.download = `quizzes.${extensions[format]}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setError(t('quizGenerator.downloadError'));
        }
    };

    const handleClear = () => {
        setSubject('');
        setUploadedFile(null);
        setAllQuizzes([]);
        setError(null);
        setInputMethod('none');
        setTaskId(null);
        setTaskStatus(null);
        setProgress({ current: 0, total: 0, message: '' });
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('quizGenerator.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('quizGenerator.description')}
                    </p>
                    

                    {/* Input Method Selection */}
                    <div className="mt-8">
                        <label className="block text-sm font-medium mb-2">
                            {t('quizGenerator.inputMethodLabel')}
                        </label>
                        <select
                            value={inputMethod}
                            onChange={(e) => {
                                setInputMethod(e.target.value);
                                setError(null);
                            }}
                            className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                            disabled={isLoading}
                        >
                            <option value="none">{t('quizGenerator.selectMethod')}</option>
                            <option value="file">{t('quizGenerator.uploadFile')}</option>
                            <option value="text">{t('quizGenerator.typeSubject')}</option>
                        </select>
                    </div>

                    {/* File Upload Section */}
                    {inputMethod === 'file' && (
                        <div className="mt-6">
                            <label className="block text-sm font-medium mb-2">
                                {t('quizGenerator.fileUploadLabel')}
                            </label>
                            <div className="flex items-center space-x-4">
                                <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-600 rounded-md cursor-pointer hover:border-brand-orange transition-colors">
                                    <Upload className="mr-2" size={20} />
                                    <span>{uploadedFile ? uploadedFile.name : t('quizGenerator.chooseFile')}</span>
                                    <input
                                        type="file"
                                        accept=".pdf,.docx,.txt"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        disabled={isLoading}
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-slate-600 mt-1">
                                {t('quizGenerator.fileFormats')}
                            </p>
                        </div>
                    )}

                    {/* Text Input Section */}
                    {inputMethod === 'text' && (
                        <div className="mt-6">
                            <label className="block text-sm font-medium mb-2">
                                {t('quizGenerator.subjectLabel')}
                            </label>
                            <textarea
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder={t('quizGenerator.subjectPlaceholder')}
                                className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[150px] resize-y rounded-md"
                                disabled={isLoading}
                            />
                        </div>
                    )}

                    {/* Configuration Options */}
                    {inputMethod !== 'none' && (
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {t('quizGenerator.numQuizzesLabel')}
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={numQuizzes}
                                    onChange={(e) => setNumQuizzes(parseInt(e.target.value))}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {t('quizGenerator.questionsPerQuizLabel')}
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={questionsPerQuiz}
                                    onChange={(e) => setQuestionsPerQuiz(parseInt(e.target.value))}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Task Status Display */}
                    {/*{taskStatus === 'processing' && (
                        <div className="mt-4 p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded-md">
                            <div className="flex items-center">
                                <Loader2 className="animate-spin mr-2" size={20} />
                                <span>{progress.message || t('quizGenerator.processing')}</span>
                            </div>
                            {progress.total > 0 && (
                                <div className="mt-2 w-full bg-blue-200 rounded-full h-2.5">
                                    <div 
                                        className="bg-blue-600 h-2.5 rounded-full" 
                                        style={{ width: `${(progress.current / progress.total) * 100}%` }}
                                    ></div>
                                </div>
                            )}
                            <p className="text-sm mt-1">
                                {t('quizGenerator.progress', { current: progress.current, total: progress.total })}
                            </p>
                        </div>
                    )}
*/}
                    {/* Action Buttons */}
                    {inputMethod !== 'none' && (
                        <div className="flex items-center space-x-4 mt-6">
                            <button
                                onClick={handleGenerate}
                                className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                                disabled={isLoading || taskStatus === 'processing'}
                            >
                                {isLoading || taskStatus === 'processing' ? (
                                    <>
                                        <Loader2 className="animate-spin mr-2" size={20} />
                                        {taskStatus === 'processing' ? t('quizGenerator.generatingButton') : t('quizGenerator.generatingButton')}
                                    </>
                                ) : (
                                    t('quizGenerator.generateButton')
                                )}
                            </button>
                            <button
                                onClick={handleClear}
                                className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                                disabled={isLoading || taskStatus === 'processing'}
                            >
                                {t('quizGenerator.clearButton')}
                            </button>
                        </div>
                    )}

                    {/* Display Generated Quizzes */}
                    {allQuizzes.length > 0 && (
                        <div className="mt-10">
                            <h4 className="text-2xl font-semibold mb-4">
                                {t('quizGenerator.generatedQuizzes')}
                            </h4>
                            
                            {allQuizzes.map((quiz, quizIndex) => (
                                <div key={quizIndex} className="mb-8 p-6 bg-slate-50 border border-slate-300 rounded-md">
                                    <h5 className="text-xl font-bold mb-4">
                                        {t('quizGenerator.quizNumber', { number: quizIndex + 1 })}
                                    </h5>
                                    
                                    {quiz.questions.map((question, qIndex) => (
                                        <div key={qIndex} className="mb-6">
                                            <p className="font-semibold mb-2">
                                                Q{qIndex + 1}: {question.question}
                                            </p>
                                            <ul className="space-y-1 ml-4">
                                                {question.options.map((option, oIndex) => (
                                                    <li
                                                        key={oIndex}
                                                        className={
                                                            oIndex === question.answer - 1
                                                                ? 'text-green-600 font-semibold'
                                                                : 'text-brand-dark'
                                                        }
                                                    >
                                                        {oIndex + 1}. {option}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            ))}

                            {/* Download Buttons */}
                            <div className="flex flex-wrap gap-4 mt-6">
                                <button
                                    onClick={() => handleDownload('json')}
                                    className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-brand-orange transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('quizGenerator.downloadJSON')}
                                </button>
                                <button
                                    onClick={() => handleDownload('docx')}
                                    className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-brand-orange transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('quizGenerator.downloadDOCX')}
                                </button>
                                <button
                                    onClick={() => handleDownload('pptx')}
                                    className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-brand-orange transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('quizGenerator.downloadPPTX')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuizGenerator;
